import PropTypes from 'prop-types'
import classNames from 'classnames'
import React, { Component, Fragment } from 'react'
import { Builder } from '@builder.io/react'

import { connect } from 'react-redux'
import memoize from 'lodash/memoize'

import { getLineItemCount } from 'base/reducers/cart'
import { getIsAdminUser } from 'base/reducers/session/user'
import { getFilteredMenusRecursively } from 'base/reducers/menus'
import { getCurrentPath, getLastPath } from 'base/reducers/pathHistory'
import { openCart, closeCart } from 'base/actions/synchronous'
import { logOut } from 'base/actions/asynchronous'
import { currencyAware } from 'base/components/currency_aware'
import routeTo from 'base/lib/route_to'

import Event from 'base/event'
import Hub from 'base/hub'
import SiteLocation from 'base/constants/site_location'
import Link from 'base/react/components/link'
import Logo from 'base/react/components/logo'
import HidePIIFromTrackers from 'base/components/hide_pii_from_trackers'

import LoadingIndicator from 'desktop/components/layout/loading_indicator'
import MenuItem from 'desktop/components/layout/menu_item'
import CartBadge from 'desktop/components/layout/cart_badge'
import AccountDropdown from 'desktop/components/layout/account_dropdown'
import { Search } from '@everlane/icon'
import FlyoutCart from 'desktop/containers/layout/flyout_cart'
import SneakPeekLoginModal from 'desktop/components/modals/sneak_peek_login_modal'
import FollowUpModal from 'desktop/containers/modals/follow_up_modal'

import { setUserActivationStatus } from 'base/lib/trackers/google_tag_manager'
import NotificationProvider from 'base/containers/notifications/provider'
import NotificationIndicator from 'base/components/notifications/indicator'
import RouterLink from 'base/components/router_link'
import Scroller from 'base/lib/scroller'

const HEADER_HEIGHT = 56
const signUpClassname = '.app-header__value-prop-badge'
const loginClassname = 'button.app-header__menu-item-link'
const algoliaSearchTextClassname = '.algolia-search__text'
const algoliaSearchIconClassname = '.algolia-search__icon'
const loggedInClassname = '.app-header__account-inner-link'

export class Header extends Component {
  static propTypes = {
    isLoading: PropTypes.bool,
    isSignedIn: PropTypes.bool.isRequired,
    isAdminUser: PropTypes.bool.isRequired,
    isOnSearchPage: PropTypes.bool.isRequired,
    userName: PropTypes.string,
    menus: PropTypes.arrayOf(PropTypes.object).isRequired,
    currentPath: PropTypes.string.isRequired,
    numberOfLineItems: PropTypes.number.isRequired,
    logOut: PropTypes.func.isRequired,
    isIpad: PropTypes.bool.isRequired,
    bannerHeightOffset: PropTypes.number,
    gender: PropTypes.string,
    showHoliday: PropTypes.bool,
    lastPath: PropTypes.string,
  }

  state = {
    isFloating: false,
    isShowingAccountDropdown: false,
    isShowingLoginModal: false,
    isShowingSignupModal: false,
    isTransparent: false,
    isHovered: false,
    textColorWhenTransparent: '',
  }

  constructor(props) {
    super(props)

    // NOTE: yeah, we have a really large number of event handler methods in this component.
    // that's a result of us relying so heavily on component state here.

    // Some of this stuff (scroll position, etc) are actually global state and should ideally be
    // handled with redux action creators. Hence the individual methods - it will make it easier
    // to change the way this stuff works in the future.

    this.renderMenuItem = this.renderMenuItem.bind(this)
    this.handleShowAccountDropdown = this.handleShowAccountDropdown.bind(this)
    this.handleHideAccountDropdown = this.handleHideAccountDropdown.bind(this)

    this.updateTransparency = this.updateTransparency.bind(this)
    this.updateTextColor = this.updateTextColor.bind(this)
  }

  componentDidMount() {
    Hub.sub(Event.Header.COLOR_CHANGE, this.updateTextColor)
    Hub.sub(Event.Header.TRANSPARENT_HEADER, this.updateTransparency)
  }

  componentWillUnmount() {
    Hub.unsub(Event.Header.COLOR_CHANGE, this.updateTextColor)
    Hub.unsub(Event.Header.TRANSPARENT_HEADER, this.updateTransparency)
  }

  componentDidUpdate = prevProps => {
    if (!prevProps.isCartOpen && this.props.isCartOpen) {
      Hub.pub(Event.Navigation.CART_DROP_DOWN_OPENED)
    } else if (prevProps.isCartOpen && !this.props.isCartOpen) {
      Hub.pub(Event.Navigation.CART_DROP_DOWN_CLOSED)
    }
  }

  updateTransparency(event, { isTransparent, textColor }) {
    this.setState({ isTransparent, textColorWhenTransparent: textColor })
  }

  updateTextColor(event, textColor) {
    this.setState({ textColorWhenTransparent: textColor })
  }

  shouldBeTransparent() {
    return this.state.isTransparent && !this.state.isFloating && !this.state.isHovered
  }

  handleAccountClick = path => {
    Hub.pub(Event.Navigation.ACCOUNT_CLICK, {
      type: path,
      menu_type: 'dropdown',
    })
  }

  handleScroll(scrollOffset) {
    const floatAt = Math.max(10, this.props.bannerHeightOffset)
    const isFloating = scrollOffset > floatAt

    if (isFloating !== this.state.isFloating) {
      this.setState({ isFloating })
    }
  }

  handleShowAccountDropdown() {
    this.setState({ isShowingAccountDropdown: true })
    Hub.pub(Event.Navigation.ACCOUNT_DROP_DOWN_OPENED)
  }

  handleHideAccountDropdown() {
    this.setState({ isShowingAccountDropdown: false })
  }

  handleShowLoginModal = () => {
    this.setState({ isShowingLoginModal: true })
  }

  handleShowSignupModal = () => {
    this.setState({ isShowingSignupModal: true })
  }

  handleHideLoginModal = () => {
    this.setState({
      isShowingLoginModal: false,
      isShowingSignupModal: false,
    })
  }

  handleAddClassnames(classNames) {
    const allOtherRightSideMenus = document.querySelectorAll(classNames.join(','))

    try {
      allOtherRightSideMenus.forEach(menu =>
        menu.classList.add(
          'app-header__menu-item-link-text--dimmed',
          'algolia-search__icon--dimmed',
        ),
      )
    } catch (err) {
      // IE 11 doesn't allow `classList` modifications for SVG elements
    }
  }

  handleRemoveClassnames(classNames) {
    const allOtherRightSideMenus = document.querySelectorAll(classNames.join(','))

    try {
      allOtherRightSideMenus.forEach(menu =>
        menu.classList.remove(
          'app-header__menu-item-link-text--dimmed',
          'algolia-search__icon--dimmed',
        ),
      )
    } catch (err) {
      // IE 11 doesn't allow `classList` modifications for SVG elements
    }
  }

  renderMenuItem(menu) {
    return (
      <MenuItem
        key={menu.id}
        name={menu.name}
        position={menu.position}
        url={menu.url}
        images={menu.images}
        submenus={menu.submenus}
        opensInNewWindow={menu.opens_in_new_window}
        currentPath={this.props.currentPath}
        isIpad={this.props.isIpad}
        shouldUseColor={!this.shouldBeTransparent()}
        gender={this.props.gender}
        showHoliday={this.props.showHoliday}
        mainNavTextColor={menu.color}
      >
        {menu.name}
      </MenuItem>
    )
  }

  // The top left section of the header deals with account-related information. Because of the
  // multiple different states that the session can be in, it requires a bit more complexity.
  renderAccountMenu() {
    if (this.props.isSignedIn) {
      const greetingText = this.props.userName ? `Hi, ${this.props.userName}` : 'Account'

      return (
        <div
          className="app-header__account-link"
          onMouseEnter={this.handleShowAccountDropdown}
          onMouseLeave={this.handleHideAccountDropdown}
        >
          <NotificationProvider>
            <NotificationIndicator />
          </NotificationProvider>

          <Link
            onClick={() => this.handleAccountClick('header')}
            inheritColor
            href="/account/info"
            className={classNames('app-header__account-inner-link', {
              'app-header__account-inner-link--active': this.state.isShowingAccountDropdown,
            })}
            onMouseEnter={() => {
              this.handleAddClassnames([algoliaSearchIconClassname, algoliaSearchTextClassname])
            }}
            onMouseLeave={() => {
              this.handleRemoveClassnames([algoliaSearchIconClassname, algoliaSearchTextClassname])
            }}
          >
            <HidePIIFromTrackers>
              <div>{greetingText}</div>
            </HidePIIFromTrackers>
          </Link>

          {this.state.isShowingAccountDropdown && (
            <AccountDropdown
              onRequestLogOut={this.props.logOut}
              showAdminLink={this.props.isAdminUser}
              onClick={this.handleAccountClick}
              hideReferralLink={!this.props.currencyIsUSD}
            />
          )}
        </div>
      )
    }

    return (
      <div className="app-header__account-link">
        <Link
          onMouseEnter={() => {
            this.handleAddClassnames([
              signUpClassname,
              algoliaSearchTextClassname,
              algoliaSearchIconClassname,
            ])
          }}
          onMouseLeave={() => {
            this.handleRemoveClassnames([
              signUpClassname,
              algoliaSearchTextClassname,
              algoliaSearchIconClassname,
            ])
          }}
          onClick={this.handleShowLoginModal}
          className="app-header__menu-item-link app-header--transparent-border"
        >
          Log In
        </Link>
        <Link
          onMouseEnter={() => {
            this.handleAddClassnames([
              loginClassname,
              algoliaSearchTextClassname,
              algoliaSearchIconClassname,
            ])
          }}
          onMouseLeave={() => {
            this.handleRemoveClassnames([
              loginClassname,
              algoliaSearchTextClassname,
              algoliaSearchIconClassname,
            ])
          }}
          onClick={this.handleShowSignupModal}
          className="app-header__value-prop-badge app-header--transparent-border"
        >
          Sign Up
        </Link>
      </div>
    )
  }

  renderCartMenu() {
    const { isCartOpen, openCart, numberOfLineItems } = this.props

    return (
      <Fragment>
        <div className="app-header__cart">
          <CartBadge
            isBeingHovered={isCartOpen}
            openCart={() => openCart()}
            numberOfLineItems={numberOfLineItems}
          />
        </div>
      </Fragment>
    )
  }

  renderFlyoutCart() {
    const { isCartOpen } = this.props

    if (isCartOpen) {
      return <FlyoutCart closeCart={() => this.props.closeCart()} />
    }

    return null
  }

  renderLoginModal() {
    return (
      <SneakPeekLoginModal
        isSigningIn={this.state.isShowingLoginModal}
        isNewUser={this.state.isShowingSignupModal ? undefined : false}
        skipEmailEntry={this.state.isShowingSignupModal}
        siteLocation={SiteLocation.LEFT_HEADER}
        onRequestClose={() => this.handleHideLoginModal()}
      />
    )
  }

  toggleSearchWithIcon = () => {
    Hub.pub(Event.AlgoliaSearch.NAV_CLICK, {
      from: window.location.pathname,
    })

    this.props.isOnSearchPage && setTimeout(() => routeTo(this.props.lastPath || '/'), 250)
  }

  renderAlgoliaSearch() {
    return (
      <RouterLink
        onMouseEnter={() => {
          this.handleAddClassnames([signUpClassname, loginClassname, loggedInClassname])
        }}
        onMouseLeave={() => {
          this.handleRemoveClassnames([signUpClassname, loginClassname, loggedInClassname])
        }}
        className={classNames('algolia-search', {
          'algolia-search--transparent': this.shouldBeTransparent(),
        })}
        href="/search"
        onClick={this.toggleSearchWithIcon}
      >
        <div className="algolia-search__wrapper">
          <Search className="algolia-search__icon" />
          <p className="algolia-search__text">Search</p>
        </div>
      </RouterLink>
    )
  }

  render() {
    const isTransparent = this.shouldBeTransparent()
    const className = classNames('app-header', {
      'app-header--transparent': isTransparent,
      'app-header--with-shadow': this.state.isFloating,
      'app-header--no-shadow': this.props.isOnSearchPage,
    })

    const menus = this.props.menus.filter(
      menu => menu.name !== 'Visit Us' && menu.name !== 'Stores' && menu.name !== 'Factories',
    )

    const headerStyles = {
      height: HEADER_HEIGHT,
      color: isTransparent ? this.state.textColorWhenTransparent : null,
      stroke: isTransparent ? this.state.textColorWhenTransparent : null,
      fill: isTransparent ? this.state.textColorWhenTransparent : null,
      transition: this.props.isOnSearchPage
        ? 'top 100ms cubic-bezier(0.45, 0.05, 0.55, 0.95)'
        : null,
    }

    return (
      <Fragment>
        {/* Setting throttle to 0 on Search to update nav offset as soon as scroll event fires */}
        <Scroller
          throttleDelay={this.props.isOnSearchPage ? 0 : 420}
          onChange={this.handleScroll.bind(this)}
          cancelsScroll={false}
        />
        <header
          className={className}
          style={headerStyles}
          onMouseEnter={() => this.setState({ isHovered: true })}
          onMouseLeave={() => this.setState({ isHovered: false })}
        >
          <div className="app-header__far-left">{menus.map(this.renderMenuItem)}</div>
          <div className="app-header__middle">
            <div className="app-header__logo-container">
              <RouterLink href="/" style={{ fill: 'inherit' }} aria-label="Everlane">
                <Logo />
              </RouterLink>
            </div>
          </div>
          <div className="app-header__far-right">
            {this.renderAlgoliaSearch()}
            {this.renderAccountMenu()}
            {this.renderCartMenu()}
          </div>
        </header>

        <LoadingIndicator numberOfActiveRequests={this.props.numberOfActiveRequests} />

        {(this.state.isShowingLoginModal || this.state.isShowingSignupModal) &&
          this.renderLoginModal()}
        {this.renderFlyoutCart()}

        {!Builder.isEditing && (
          <FollowUpModal
            emailEnteredEvent={Event.Wunderkind.EMAIL_ENTERED}
            siteLocation={SiteLocation.WUNDERKIND_MODAL}
          />
        )}
      </Fragment>
    )
  }
}

const getComputedMenusByIds = memoize((ids, menus, platform, placement) => {
  return getFilteredMenusRecursively(menus, platform, placement)
})

function mapStateToProps(state) {
  const currentPath = getCurrentPath(state)
  setUserActivationStatus(state?.session?.visitor?.segment)

  return {
    numberOfActiveRequests: state.activeRequests,
    numberOfLineItems: getLineItemCount(state.cart),
    menus: getComputedMenusByIds(
      state.menus.main.map(m => m.id).join('-'),
      state.menus.main,
      'desktop',
      'top',
    ),
    currentPath,
    isIpad: navigator.userAgent.includes('iPad'),
    isSignedIn: !!state.session.user,
    isAdminUser: getIsAdminUser(state.session.user),
    userName: state.session.user ? state.session.user.first_name : null,
    gender: state.session.user ? state.session.user.gender : null,
    isCartOpen: state.cart.isOpen,
    isOnSearchPage: currentPath === '/search',
    lastPath: getLastPath(state),
    lineItemAdded: !!state.cart.isBlocked,
  }
}

function mapDispatchToProps(dispatch) {
  return {
    logOut: () => {
      dispatch(logOut()).then(() => routeTo('/', { hard: true }))
    },
    openCart: () => dispatch(openCart()),
    closeCart: () => dispatch(closeCart()),
  }
}

export default currencyAware(connect(mapStateToProps, mapDispatchToProps)(Header))

export { HEADER_HEIGHT }
